// src/pages/Attendance.js
import { useState, useCallback, useRef } from "react";
import { saveAttendance } from "../firebase/services";
import { useAuth } from "../context/AuthContext";
import { Btn, Tag, Card, Avatar, PageHeader, Alert, Spinner, ProgressBar } from "../components/UI";
import * as XLSX from "xlsx";

const today = () => new Date().toISOString().split("T")[0];
const fmt = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS_COLOR = { present: "var(--success)", absent: "var(--danger)", late: "var(--warn)" };
const NEXT_STATUS  = { present: "absent", absent: "late", late: "present" };

export default function Attendance({ employees, attendance, setAttendance }) {
  const { canEdit } = useAuth();
  const currentEmps = employees.filter(e => e.status === "current");

  const [selDate, setSelDate] = useState(today());
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState({ text: "", type: "info" });
  const xlsRef = useRef();

  const flash = (text, type = "success") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "", type: "info" }), 4000); };

  // Build records for selected date (merge saved + current employees)
  const getRecords = useCallback((date) => {
    const saved = attendance.find(a => a.date === date);
    const byId = saved ? Object.fromEntries(saved.records.map(r => [r.empId, r])) : {};
    return currentEmps.map(e => byId[e.id] || { empId: e.id, status: "present" });
  }, [attendance, currentEmps]);

  const [records, setRecords] = useState(() => getRecords(today()));

  const changeDate = (date) => { setSelDate(date); setRecords(getRecords(date)); };

  const toggle = (empId) => {
    if (!canEdit) return;
    setRecords(prev => prev.map(r => r.empId === empId ? { ...r, status: NEXT_STATUS[r.status] } : r));
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveAttendance(selDate, records);
      setAttendance(prev => {
        const idx = prev.findIndex(a => a.date === selDate);
        const entry = { date: selDate, records };
        return idx >= 0 ? prev.map((a, i) => i === idx ? entry : a) : [...prev, entry];
      });
      flash(`✅ Attendance saved for ${fmt(selDate)}`);
    } catch (e) {
      flash("❌ Save failed: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Excel / CSV import ────────────────────────────────────────
  const handleXlsUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb   = XLSX.read(ev.target.result, { type: "binary" });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // Find header row (look for "name" and "status" columns)
        let headerRow = 0;
        let nameCol = 0, statusCol = 1;
        for (let i = 0; i < Math.min(5, rows.length); i++) {
          const row = rows[i].map(c => String(c || "").toLowerCase());
          const ni = row.findIndex(c => c.includes("name"));
          const si = row.findIndex(c => c.includes("status") || c.includes("attendance"));
          if (ni >= 0 && si >= 0) { headerRow = i; nameCol = ni; statusCol = si; break; }
        }

        const parsed = [];
        for (let i = headerRow + 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || !row[nameCol]) continue;
          const nameRaw   = String(row[nameCol] || "").trim();
          const statusRaw = String(row[statusCol] || "present").trim().toLowerCase();

          const emp = currentEmps.find(e =>
            e.name.toLowerCase() === nameRaw.toLowerCase() ||
            e.name.toLowerCase().includes(nameRaw.toLowerCase())
          );
          if (!emp) continue;

          const status = ["present", "absent", "late", "p", "a", "l"].includes(statusRaw)
            ? (statusRaw === "p" ? "present" : statusRaw === "a" ? "absent" : statusRaw === "l" ? "late" : statusRaw)
            : "present";

          parsed.push({ empId: emp.id, status });
        }

        // Fill any employees not in the file
        const parsedIds = parsed.map(r => r.empId);
        currentEmps.forEach(e => { if (!parsedIds.includes(e.id)) parsed.push({ empId: e.id, status: "present" }); });

        setRecords(parsed);
        flash(`✅ Imported ${parsed.length} records from "${file.name}". Review & save.`);
      } catch (err) {
        flash("❌ Could not read file: " + err.message, "error");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  const present = records.filter(r => r.status === "present").length;
  const absent  = records.filter(r => r.status === "absent").length;
  const late    = records.filter(r => r.status === "late").length;
  const pct     = currentEmps.length > 0 ? Math.round((present / currentEmps.length) * 100) : 0;

  return (
    <div style={{ animation: "fadeUp .35s ease" }}>
      <PageHeader
        title="Attendance Tracker"
        sub="Mark, import via Excel, and review daily attendance"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            {canEdit && (
              <>
                <Btn variant="ghost" size="sm" onClick={() => xlsRef.current.click()}>📤 Upload Excel / CSV</Btn>
                <input ref={xlsRef} type="file" accept=".csv,.xls,.xlsx" style={{ display: "none" }} onChange={handleXlsUpload} />
                <Btn size="sm" onClick={save} disabled={saving}>
                  {saving ? <><Spinner size={13} color="#fff" /> Saving…</> : "💾 Save"}
                </Btn>
              </>
            )}
          </div>
        }
      />

      {msg.text && <div style={{ marginBottom: 16 }}><Alert type={msg.type === "error" ? "error" : "success"}>{msg.text}</Alert></div>}

      {/* Excel format guide */}
      <Card style={{ marginBottom: 20, background: "#faf8f4", padding: "14px 18px", border: "1px dashed #d4d0c8" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>Excel / CSV Format</div>
        <div style={{ fontSize: 12.5, color: "var(--slate)", lineHeight: 1.7 }}>
          Upload an Excel (.xlsx) or CSV file with columns: <b>Name</b> and <b>Status</b> (present / absent / late, or p / a / l).<br />
          Row 1 should be the header. Employee names must match records in the system.
        </div>
      </Card>

      {/* Date + stats */}
      <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap", alignItems: "stretch" }}>
        <Card style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--slate-light)" }}>Date</span>
          <input type="date" value={selDate} onChange={e => changeDate(e.target.value)}
            style={{ padding: "6px 10px", border: "1.5px solid #d4d0c8", borderRadius: 8, fontSize: 14, background: "#fff", fontFamily: "'DM Sans',sans-serif" }} />
        </Card>
        {[["Present", present, "var(--success)"], ["Absent", absent, "var(--danger)"], ["Late", late, "var(--warn)"]].map(([l, v, c]) => (
          <Card key={l} style={{ display: "flex", flexDirection: "column", justifyContent: "center", padding: "10px 20px", minWidth: 90 }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: c, fontFamily: "'Syne',sans-serif", lineHeight: 1 }}>{v}</div>
            <div style={{ fontSize: 11, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .5 }}>{l}</div>
          </Card>
        ))}
        <Card style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 160 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
            <span style={{ color: "var(--slate-light)" }}>Attendance rate</span>
            <b style={{ color: "var(--success)" }}>{pct}%</b>
          </div>
          <ProgressBar value={pct} color="var(--success)" height={8} />
        </Card>
      </div>

      {/* Attendance table */}
      <Card style={{ padding: 0, overflow: "hidden", marginBottom: 32 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--cream)", fontSize: 11, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6 }}>
                {["Employee", "Role", "Department", "Status", canEdit ? "Toggle" : ""].map(h => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentEmps.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: "var(--slate-light)" }}>No current employees. Add staff first.</td></tr>
              )}
              {currentEmps.map(e => {
                const rec = records.find(r => r.empId === e.id) || { status: "present" };
                return (
                  <tr key={e.id} style={{ borderTop: "1px solid #f0ece3" }}>
                    <td style={{ padding: "11px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Avatar name={e.name} size={30} photo={e.photoURL} />
                        <span style={{ fontSize: 14, fontWeight: 500 }}>{e.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: "11px 16px", fontSize: 13 }}>{e.role || "—"}</td>
                    <td style={{ padding: "11px 16px", fontSize: 13 }}>{e.dept || "—"}</td>
                    <td style={{ padding: "11px 16px" }}>
                      <Tag label={rec.status.charAt(0).toUpperCase() + rec.status.slice(1)} color={STATUS_COLOR[rec.status]} />
                    </td>
                    {canEdit && (
                      <td style={{ padding: "11px 16px" }}>
                        <button onClick={() => toggle(e.id)}
                          title="Cycle: Present → Absent → Late → Present"
                          style={{ padding: "4px 12px", borderRadius: 6, border: "1.5px solid #c8c4bc", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--slate)", transition: "all .2s" }}>
                          Cycle ↻
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* History */}
      {attendance.length > 0 && (
        <div>
          <h3 style={{ fontSize: 18, fontFamily: "'Syne',sans-serif", marginBottom: 16 }}>Attendance History</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[...attendance].sort((a, b) => b.date.localeCompare(a.date)).map(a => {
              const p  = a.records.filter(r => r.status === "present").length;
              const ab = a.records.filter(r => r.status === "absent").length;
              const lt = a.records.filter(r => r.status === "late").length;
              const pct = a.records.length > 0 ? Math.round((p / a.records.length) * 100) : 0;
              return (
                <Card key={a.date} style={{ padding: "12px 18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 700, fontSize: 15, minWidth: 110 }}>{fmt(a.date)}</div>
                    <div style={{ flex: 1, minWidth: 80 }}><ProgressBar value={pct} color="var(--success)" /></div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Tag label={`✓ ${p}`} color="var(--success)" />
                      <Tag label={`✗ ${ab}`} color="var(--danger)" />
                      {lt > 0 && <Tag label={`⌚ ${lt}`} color="var(--warn)" />}
                    </div>
                    <Btn size="xs" variant="ghost" onClick={() => changeDate(a.date)}>View</Btn>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
